// ChatThread.tsx — orchestrates message atoms + inline principle expansion

import type { JSX, ReactNode } from 'react';
import React, { useRef, useEffect, useState } from 'react';
import { useTokens } from '../state/theme';
import { useReducedMotion } from '../state/use-reduced-motion';
import type { Message, Citation, Principle } from '../types/domain';
import { ProfMessage } from './ProfMessage';
import { TTSButton } from './TTSButton';
import { YouMessage } from './YouMessage';
import { StreamingMessage } from './StreamingMessage';
import { QuestionCard } from './QuestionCard';
import { PrincipleInlineCard } from './PrincipleInlineCard';
import { ProfProse } from './ProfProse';
import { CitationChip } from './CitationChip';
import { Regola } from './Regola';
import { ActionChips } from './ActionChips';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

export function ChatScroll({
  children,
  paddingTop = 14,
}: {
  children: ReactNode;
  paddingTop?: number;
}): JSX.Element {
  return (
    <div
      className="asc-scroll"
      style={{
        flex: 1,
        overflow: 'auto',
        padding: `${paddingTop}px 16px 16px`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Prof message that's actively streaming — renders accumulated text via ProfProse
 * PLUS an inline "typing" caret so the student sees the message build live. This
 * replaces the old all-or-nothing behavior where the streaming state showed ONLY
 * dots until `replace_text` swapped in the final text.
 */
function StreamingProfMessage({ text }: { text: string }): JSX.Element {
  const tokens = useTokens();
  return (
    <ProfMessage>
      <ProfProse text={text} />
      {/* Pulsing caret appended at the end of the stream */}
      <span
        aria-label="streaming"
        style={{
          display: 'inline-block',
          marginLeft: 2,
          width: 7,
          height: 16,
          background: tokens.accent,
          verticalAlign: '-3px',
          animation: 'prof-blink 0.9s infinite ease-in-out',
          borderRadius: 1,
        }}
      />
    </ProfMessage>
  );
}

function SystemDivider({ text }: { text: string }): JSX.Element {
  const tokens = useTokens();
  // Color-code common status messages for immediate visual feedback: green for
  // correct (✓), red for wrong (✗). Everything else stays neutral.
  const isCorrect = text.startsWith('✓');
  const isWrong = text.startsWith('✗');
  const color = isCorrect ? tokens.right : isWrong ? tokens.wrong : tokens.textFaint;
  const lineColor = isCorrect ? tokens.right : isWrong ? tokens.wrong : tokens.line;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '10px 0 4px',
      }}
      role="status"
    >
      <div style={{ flex: 1, height: 1, background: lineColor, opacity: isCorrect || isWrong ? 0.4 : 1 }} />
      <div
        style={{
          fontFamily: tokens.fonts.mono,
          fontSize:   isCorrect || isWrong ? 11 : 9.5,
          fontWeight: isCorrect || isWrong ? 700 : 400,
          letterSpacing: 0.7,
          color,
          textTransform: 'uppercase',
        }}
      >
        {text}
      </div>
      <div style={{ flex: 1, height: 1, background: lineColor, opacity: isCorrect || isWrong ? 0.4 : 1 }} />
    </div>
  );
}

function CitationRow({
  citations,
  onCite,
}: {
  citations: Citation[];
  onCite: (c: Citation) => void;
}): JSX.Element | null {
  if (!citations.length) return null;
  // V28 S1 — the prototype renders citation chips as a bare `.chiprow` INSIDE
  // the Prof bubble, directly under the prose (prof-chat.html §49-77): no mono
  // "Principi" eyebrow, just the `.chip.cite` pill(s). The chip itself carries
  // the brand-ink + bookmark look (CitationChip).
  return (
    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {citations.map((c) => (
        <CitationChip key={c.id} cite={c} onClick={() => onCite(c)} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ExpandedPrinciple {
  messageId: string;
  principleId: string;
}

export interface ChatThreadProps {
  messages: Message[];
  expandedPrinciple: ExpandedPrinciple | null;
  onExpandPrinciple: (messageId: string, principleId: string) => void;
  /** Structured Prof provenance opens its canonical Appendice destination. */
  onOpenCitation?: (citation: Citation) => void;
  onCollapsePrinciple: () => void;
  resolvedPrinciple: Principle | null;
  onAction?: (actionId: string) => void;
  feedbackByMessageId?: Record<string, ProfFeedbackSignal>;
  onProfFeedback?: (messageId: string, feedback: ProfFeedbackSignal | null) => void;
  /** Ask the server to issue an official, canonical checkpoint for this answer. */
  onRequestCheckpoint?: (messageId: string, sessionId: string) => void;
  /** Server-issued checkpoint UI, keyed by the persisted Prof message id. */
  checkpointByMessageId?: Record<string, ReactNode>;
  /** Transient issue state; no checkpoint content is invented client-side. */
  checkpointIssueStateByMessageId?: Record<string, 'pending' | 'error'>;
  /**
   * V28 S1 (additive, optional) — host an inline borderless "Regola d'oro"
   * callout INSIDE a specific Prof bubble (prototype prof-chat.html §63-67).
   * Keyed by message id → the rule node (e.g. <Regola>…</Regola> or its text).
   * Rendered under the prose + citations, before the follow-up chips. Hosts that
   * don't carry per-message rules simply omit this; nothing renders.
   */
  regolaByMessageId?: Record<string, ReactNode>;
  /**
   * V28 S1 (additive, optional) — follow-up chips hosted IN-BUBBLE under the
   * LATEST completed Prof text answer (prototype prof-chat.html §79-84). Each
   * chip sends a turn via `onFollowUp` (none navigate). When omitted (or empty)
   * no chips render and hosts can keep showing follow-ups below the thread.
   */
  followUps?: string[];
  /** Click handler for an in-bubble follow-up chip (receives the chip label). */
  onFollowUp?: (question: string) => void;
}

export type ProfFeedbackSignal = 'useful' | 'confusing';

// ---------------------------------------------------------------------------
// Render helper
// ---------------------------------------------------------------------------

function renderMessage(
  msg: Message,
  props: ChatThreadProps,
  latestFeedbackMessageId: string | null,
): JSX.Element | null {
  if (msg.authorKind === 'you') {
    // 'you' messages always have text (kind === 'text' or undefined)
    const text = 'text' in msg ? msg.text : '';
    return <YouMessage>{text}</YouMessage>;
  }

  if (msg.authorKind === 'system') {
    return <SystemDivider text={msg.text} />;
  }

  // authorKind === 'prof'
  if (msg.kind === 'streaming') {
    // If tokens have started accumulating, render them live (with dots trailing)
    // so the student can follow along — the Claude.ai-style token-by-token feel.
    // Otherwise (empty text, pre-first-token) show the standalone "Il Prof sta
    // scrivendo…" indicator.
    if (msg.text && msg.text.length > 0) {
      return <StreamingProfMessage text={msg.text} />;
    }
    return <StreamingMessage />;
  }

  if (msg.kind === 'question') {
    const onAnswer = (vf: 'Vero' | 'Falso') => props.onAction?.(vf === 'Vero' ? 'answer-vero' : 'answer-falso');
    return (
      <QuestionCard
        question={msg.payload}
        qIndex={msg.qIndex}
        qTotal={msg.qTotal}
        answered={msg.answered}
        onAnswer={onAnswer}
        isLeech={msg.isLeech}
        isContrastivePair={msg.isContrastivePair}
      />
    );
  }

  // Default: prof text message
  const tone = 'tone' in msg ? msg.tone : undefined;
  const citations = 'citations' in msg ? msg.citations : undefined;
  const text = 'text' in msg ? msg.text : '';
  const feedbackValue = props.feedbackByMessageId?.[msg.id];
  const checkpoint = props.checkpointByMessageId?.[msg.id];
  const checkpointIssueState = props.checkpointIssueStateByMessageId?.[msg.id];
  const canRequestCheckpoint = props.onRequestCheckpoint
    && msg.feedbackEligible === true
    && !!msg.checkpointSessionId
    && !!citations?.length;
  const showFeedbackControls = props.onProfFeedback
    && msg.feedbackEligible === true
    && msg.id === latestFeedbackMessageId;
  const showUnsavedWarning = msg.assistantPersistenceOutcome === 'locally_retained_unsaved'
    || msg.assistantPersistenceOutcome === 'failed';
  // V28 S1: optional per-bubble Regola d'oro callout + in-bubble follow-up chips.
  // Regola is hosted under the prose (before citations); follow-ups sit at the
  // bottom of the LATEST completed Prof answer, matching the prototype.
  const regola = props.regolaByMessageId?.[msg.id];
  const showFollowUps =
    !!props.onFollowUp
    && !!props.followUps?.length
    && msg.id === latestFeedbackMessageId;

  return (
    <ProfMessage tone={tone}>
      <ProfProse
        text={text}
        onCite={(id) => props.onExpandPrinciple(msg.id, id)}
      />
      {regola != null && (
        <div style={{ marginTop: 12 }}>
          {typeof regola === 'string' ? <Regola>{regola}</Regola> : regola}
        </div>
      )}
      {citations && citations.length > 0 && (
        <CitationRow
          citations={citations}
          onCite={(c) => {
            if (props.onOpenCitation) props.onOpenCitation(c);
            else props.onExpandPrinciple(msg.id, c.id);
          }}
        />
      )}
      {showUnsavedWarning && (
        <div
          role="status"
          style={{
            marginTop: 10,
            borderRadius: 10,
            padding: '9px 11px',
            background: 'rgba(196, 111, 0, 0.10)',
            color: '#7a4600',
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          Risposta salvata solo su questo dispositivo. Non chiudere la conversazione finché non hai riprovato.
        </div>
      )}
      {checkpoint != null ? (
        checkpoint
      ) : canRequestCheckpoint ? (
        <CheckpointIssueButton
          state={checkpointIssueState}
          onClick={() => props.onRequestCheckpoint!(msg.id, msg.checkpointSessionId!)}
        />
      ) : null}
      {showFollowUps && (
        <ActionChips
          actions={props.followUps!.map((q) => ({
            label: q,
            onClick: () => props.onFollowUp!(q),
          }))}
        />
      )}
      {text && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <TTSButton text={text} />
          {showFeedbackControls ? (
            <ProfFeedbackControls
              messageId={msg.id}
              value={feedbackValue}
              onFeedback={props.onProfFeedback}
            />
          ) : feedbackValue ? (
            <ProfFeedbackStatus messageId={msg.id} value={feedbackValue} />
          ) : null}
        </div>
      )}
    </ProfMessage>
  );
}

function CheckpointIssueButton({
  state,
  onClick,
}: {
  state?: 'pending' | 'error';
  onClick: () => void;
}): JSX.Element {
  const tokens = useTokens();
  const pending = state === 'pending';
  const label = pending
    ? 'Sto preparando la verifica ufficiale'
    : state === 'error'
      ? 'Riprova verifica ufficiale'
      : 'Verifica ufficiale';

  return (
    <button
      type="button"
      disabled={pending}
      aria-label={label}
      onClick={onClick}
      style={{
        marginTop: 10,
        minHeight: 44,
        padding: '8px 12px',
        borderRadius: tokens.radius.pill,
        border: `1px solid ${tokens.line}`,
        background: tokens.surface,
        color: tokens.text,
        cursor: pending ? 'default' : 'pointer',
        fontFamily: tokens.fonts.body,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {label}
    </button>
  );
}

function ProfFeedbackControls({
  messageId,
  value,
  onFeedback,
}: {
  messageId: string;
  value?: ProfFeedbackSignal;
  onFeedback: (messageId: string, feedback: ProfFeedbackSignal | null) => void;
}): JSX.Element {
  const tokens = useTokens();
  const options: Array<{ value: ProfFeedbackSignal; label: string; aria: string }> = [
    { value: 'useful', label: 'Utile', aria: 'Segna risposta utile' },
    { value: 'confusing', label: 'Non chiara', aria: 'Segna risposta non chiara' },
  ];

  return (
    <div
      data-testid={`prof-feedback-${messageId}`}
      role="group"
      aria-label="Feedback ultima risposta del Prof"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
      }}
    >
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-label={option.aria}
            aria-pressed={selected}
            onClick={() => onFeedback(messageId, selected ? null : option.value)}
            style={{
              minHeight: 40,
              minWidth: 64,
              padding: '6px 10px',
              borderRadius: tokens.radius.pill,
              border: `1px solid ${selected ? tokens.accent : tokens.line}`,
              background: selected ? tokens.accentSoft : 'transparent',
              color: selected ? tokens.text : tokens.textMuted,
              cursor: 'pointer',
              fontFamily: tokens.fonts.body,
              fontSize: 11.5,
              fontWeight: selected ? 700 : 600,
            }}
          >
            {option.label}
          </button>
        );
      })}
      {value && (
        <ProfFeedbackStatus messageId={messageId} value={value} />
      )}
    </div>
  );
}

function ProfFeedbackStatus({
  messageId,
  value,
}: {
  messageId: string;
  value: ProfFeedbackSignal;
}): JSX.Element {
  const tokens = useTokens();
  return (
    <span
      data-testid={`prof-feedback-status-${messageId}`}
      role="status"
      style={{
        color: tokens.textFaint,
        fontFamily: tokens.fonts.body,
        fontSize: 11.5,
        alignSelf: 'center',
      }}
    >
      Salvato: {value === 'useful' ? 'utile' : 'non chiara'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ChatThread
// ---------------------------------------------------------------------------

export function ChatThread(props: ChatThreadProps): JSX.Element {
  const tokens = useTokens();
  // FP9 a11y wiring: honour reduced-motion (OS pref OR the in-app toggle) for
  // the jump-to-bottom scroll. The auto-stick during streaming already uses an
  // instant `scrollTop =` assignment (no animation); only the explicit
  // jump-to-bottom animates, so that's where smooth→auto matters.
  const reducedMotion = useReducedMotion();
  const scrollRef = useRef<HTMLDivElement>(null);
  // Tracks whether the user is currently "pinned" to the bottom of the thread.
  // Updated on scroll: if they scroll up, we stop auto-scrolling (so we don't
  // yank them away from reading an older message). If they scroll back to the
  // bottom, we resume. New messages and streaming tokens will auto-scroll only
  // when pinnedRef is true.
  const pinnedRef = useRef(true);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);

  // Hash the last message's text length + total count so this effect runs whenever
  // either (a) a new message arrives or (b) the last message's text grows (streaming).
  const tail = props.messages[props.messages.length - 1];
  const tailSignature = tail && 'text' in tail ? `${props.messages.length}-${tail.text.length}` : `${props.messages.length}`;
  const latestFeedbackMessageId = getLatestCompletedProfTextId(props.messages);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (pinnedRef.current) {
      // Auto-stick to bottom during streams / on new messages.
      el.scrollTop = el.scrollHeight;
      return;
    }
    // Not pinned — user scrolled up. If new content arrived below them, surface
    // the floating "jump to bottom" chip so they know there's something new
    // without yanking them.
    const scrolledAwayFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight >= 80;
    if (scrolledAwayFromBottom) setShowJumpToBottom(true);
  }, [tailSignature]);

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    // 80px tolerance — "near-bottom" counts as pinned.
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    pinnedRef.current = nearBottom;
    if (nearBottom) setShowJumpToBottom(false);
  }

  function jumpToBottom() {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: reducedMotion ? 'auto' : 'smooth' });
    pinnedRef.current = true;
    setShowJumpToBottom(false);
  }

  return (
    <div style={{ flex: 1, position: 'relative', display: 'flex', minHeight: 0, overflow: 'hidden' }}>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="asc-scroll"
        style={{
          flex:     1,
          overflow: 'auto',
          padding:  '14px 16px 16px',
        }}
      >
        {props.messages.map((msg) => (
          <React.Fragment key={msg.id}>
            {renderMessage(msg, props, latestFeedbackMessageId)}
            {props.expandedPrinciple?.messageId === msg.id &&
              props.resolvedPrinciple && (
                <PrincipleInlineCard
                  principle={props.resolvedPrinciple}
                  onClose={props.onCollapsePrinciple}
                />
              )}
          </React.Fragment>
        ))}
      </div>
      {showJumpToBottom && (
        <button
          onClick={jumpToBottom}
          aria-label="Vai in fondo"
          style={{
            position:       'absolute',
            bottom:         12,
            right:          14,
            display:        'flex',
            alignItems:     'center',
            gap:            6,
            padding:        '8px 12px',
            background:     tokens.accent,
            color:          tokens.mode === 'dark' ? '#1a1510' : '#fff',
            border:         'none',
            borderRadius:   999,
            fontFamily:     tokens.fonts.body,
            fontSize:       12,
            fontWeight:     600,
            cursor:         'pointer',
            boxShadow:      '0 2px 8px rgba(0,0,0,0.2)',
            zIndex:         2,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 5l3 3 3-3"/>
          </svg>
          Nuovi messaggi
        </button>
      )}
    </div>
  );
}

function getLatestCompletedProfTextId(messages: Message[]): string | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.authorKind === 'prof' && (message.kind === 'streaming' || message.kind === 'question')) {
      return null;
    }
    if (
      message.authorKind === 'prof'
      && 'text' in message
      && message.text.trim()
    ) {
      return message.id;
    }
  }
  return null;
}
